import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Briefcase, TrendingUp, Users, Award, ArrowRight, CheckCircle2 } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useSEO } from '../hooks/useSEO';
import { REGISTER_URL } from '../config/app';

export default function BusinessSupport() {
  const { t } = useLanguage();
  useSEO({
    title: 'Business Support',
    description:
      'GRAWEO provides grants, training, and mentorship for entrepreneurs and small businesses affected by conflict, helping rebuild economic independence.',
  });

  const programs = [
    {
      icon: Briefcase,
      title: t('business_program_training'),
      description: t('business_program_training_desc'),
    },
    {
      icon: TrendingUp,
      title: t('business_program_mentorship'),
      description: t('business_program_mentorship_desc'),
    },
    {
      icon: Users,
      title: t('business_program_networking'),
      description: t('business_program_networking_desc'),
    },
    {
      icon: Award,
      title: t('business_program_funding'),
      description: t('business_program_funding_desc'),
    },
  ];

  const steps = [
    {
      number: 1,
      title: t('business_step_assessment'),
      description: t('business_step_assessment_desc'),
    },
    {
      number: 2,
      title: t('business_step_planning'),
      description: t('business_step_planning_desc'),
    },
    {
      number: 3,
      title: t('business_step_implementation'),
      description: t('business_step_implementation_desc'),
    },
    {
      number: 4,
      title: t('business_step_monitoring'),
      description: t('business_step_monitoring_desc'),
    },
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-graweo-chocolate to-graweo-olive text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold font-manrope mb-6">
              {t('nav_business')}
            </h1>
            <p className="text-xl text-gray-100 leading-relaxed mb-8">
              {t('business_hero_subtitle')}
            </p>
            <a href={REGISTER_URL}>
              <Button variant="secondary" size="lg">
                {t('business_apply_now')} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-gray-900 mb-6">
                {t('business_overview_title')}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                {t('business_overview_text')}
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-graweo-olive flex-shrink-0 mt-1" />
                  <span className="text-gray-700">{t('business_benefit_1')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-graweo-olive flex-shrink-0 mt-1" />
                  <span className="text-gray-700">{t('business_benefit_2')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-graweo-olive flex-shrink-0 mt-1" />
                  <span className="text-gray-700">{t('business_benefit_3')}</span>
                </li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-graweo-olive/10 to-graweo-tan/10 rounded-3xl p-8 flex items-center justify-center min-h-64">
              <Briefcase className="w-32 h-32 text-graweo-chocolate opacity-20" />
            </div>
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-gray-900 mb-12 text-center">
            {t('business_programs_title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {programs.map((program, index) => {
              const Icon = program.icon;
              return (
                <Card key={index} className="p-8 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-br from-graweo-chocolate to-graweo-olive text-white">
                        <Icon className="w-6 h-6" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold font-manrope text-gray-900 mb-2">
                        {program.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {program.description}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-gray-900 mb-12 text-center">
            {t('business_process_title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <Card className="p-6 text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-graweo-terra to-graweo-terra/80 rounded-full flex items-center justify-center text-white font-bold text-lg mx-auto mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-lg font-bold font-manrope text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {step.description}
                  </p>
                </Card>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/4 -right-4 w-8 h-0.5 bg-gradient-to-r from-graweo-chocolate to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-gray-900 mb-8 text-center">
              {t('business_eligibility_title')}
            </h2>
            <Card className="p-8">
              <ul className="space-y-4">
                <li className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-graweo-chocolate text-white flex items-center justify-center text-sm font-bold mt-1">
                    ✓
                  </div>
                  <span className="text-gray-700">{t('business_eligibility_1')}</span>
                </li>
                <li className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-graweo-chocolate text-white flex items-center justify-center text-sm font-bold mt-1">
                    ✓
                  </div>
                  <span className="text-gray-700">{t('business_eligibility_2')}</span>
                </li>
                <li className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-graweo-chocolate text-white flex items-center justify-center text-sm font-bold mt-1">
                    ✓
                  </div>
                  <span className="text-gray-700">{t('business_eligibility_3')}</span>
                </li>
                <li className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-graweo-chocolate text-white flex items-center justify-center text-sm font-bold mt-1">
                    ✓
                  </div>
                  <span className="text-gray-700">{t('business_eligibility_4')}</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-graweo-chocolate to-graweo-olive text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold font-manrope mb-6">
            {t('business_cta_title')}
          </h2>
          <p className="text-xl text-gray-100 mb-8 max-w-2xl mx-auto">
            {t('business_cta_text')}
          </p>
          <a href={REGISTER_URL}>
            <Button variant="secondary" size="lg">
              {t('business_apply_now')} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
